// providerAuth.ts — sesión del PROVEEDOR self-service del marketplace (S3 P1).
//
// Store SEPARADO de stores/auth.ts (staff de clínica) y de la sesión del tutor
// (services/tutor.ts) — usa services/provider.ts, que persiste su token en su
// propia clave de sessionStorage. Nunca comparte estado con el store de staff.
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import {
  asProviderApiError,
  getProviderToken,
  providerService,
  setProviderToken,
  type ProviderMe,
} from '@/services/provider';

export const useProviderAuthStore = defineStore('providerAuth', () => {
  const me = ref<ProviderMe | null>(null);
  const bootstrapped = ref(false);
  const error = ref<string | null>(null);

  const isAuthenticated = computed(() => me.value !== null);
  const providerName = computed(() => me.value?.provider?.name ?? '');
  const providerCategory = computed(() => me.value?.provider?.category ?? '');
  const email = computed(() => me.value?.email ?? '');
  const role = computed(() => me.value?.role ?? '');

  async function fetchMe(): Promise<boolean> {
    try {
      me.value = await providerService.me();
      return true;
    } catch {
      me.value = null;
      return false;
    }
  }

  async function bootstrap(): Promise<void> {
    if (bootstrapped.value) return;
    if (getProviderToken()) {
      const ok = await fetchMe();
      if (!ok) setProviderToken(null);
    }
    bootstrapped.value = true;
  }

  async function login(emailInput: string, password: string) {
    error.value = null;
    try {
      await providerService.login(emailInput, password);
      const ok = await fetchMe();
      if (!ok) {
        error.value = 'No se pudo cargar la sesión';
        return { ok: false as const, error: error.value };
      }
      bootstrapped.value = true;
      return { ok: true as const };
    } catch (e) {
      const err = asProviderApiError(e);
      error.value =
        err.code === 'RATE_LIMITED'
          ? 'Demasiados intentos, intenta más tarde'
          : 'Email o contraseña incorrectos';
      return { ok: false as const, error: error.value };
    }
  }

  async function activate(token: string, password: string) {
    error.value = null;
    try {
      await providerService.activate(token, password);
      const ok = await fetchMe();
      if (!ok) {
        error.value = 'No se pudo cargar la sesión';
        return { ok: false as const, error: error.value };
      }
      bootstrapped.value = true;
      return { ok: true as const };
    } catch (e) {
      const err = asProviderApiError(e);
      error.value =
        err.code === 'RATE_LIMITED'
          ? 'Demasiados intentos, intenta más tarde'
          : 'El enlace no es válido o ya fue usado. Solicita una nueva invitación.';
      return { ok: false as const, error: error.value };
    }
  }

  function logout(): void {
    providerService.logout();
    me.value = null;
    bootstrapped.value = true;
  }

  // Sesión revocada por el backend (401 en cualquier request autenticado) — el
  // interceptor de services/provider.ts limpia el token y dispara este evento;
  // acá limpiamos el estado del store para que la UI reaccione.
  window.addEventListener('wiservet:provider-session-expired', () => {
    me.value = null;
  });

  return {
    me,
    bootstrapped,
    error,
    isAuthenticated,
    providerName,
    providerCategory,
    email,
    role,
    fetchMe,
    bootstrap,
    login,
    activate,
    logout,
  };
});
